import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useTranslation } from 'react-i18next';
import { userJobsApi, resumeInfoApi, userProfileApi } from '../../services/api';
import {
  FileText,
  Bookmark,
  CheckCircle,
  Star,
  TrendingUp,
  X,
  Clock,
  Search,
  Target,
  Sparkles,
  Award,
  Zap,
  Settings,
  User,
  Crown
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function RightSidebar({ isOpen, onClose }) {
  const { currentUser, userData } = useAuth();
  const { subscription, hasActiveSubscription, getCurrentPlan } = useSubscription();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const [userProfile, setUserProfile] = useState(null);
  const [stats, setStats] = useState({
    totalResumes: 0,
    savedJobs: 0,
    applications: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const translations = {
    en: {
      profile: 'Profile',
      proMember: 'Premium',
      basicMember: 'Basic',
      freePlan: 'Free Plan',
      accountStats: 'Account Stats',
      resumes: 'Resumes',
      savedJobs: 'Saved Jobs',
      applications: 'Applications',
      recentActivity: 'Recent Activity',
      viewProfile: 'View Profile',
      editProfile: 'Edit Profile',
      upgradeNow: 'Upgrade Plan',
      unlockFeatures: 'Unlock unlimited resumes, AI features, and priority support',
      noActivity: 'No recent activity',
      memberSince: 'Member Since',
      emailStatus: 'Email Status',
      verified: 'Verified',
      notVerified: 'Not Verified',
      quickStats: 'Quick Stats',
      quickActions: 'Quick Actions',
      settings: 'Settings',
      resumeUpdated: 'Resume Updated',
      jobSaved: 'Job Saved',
      aiEnhancement: 'AI Enhancement',
      enhancedWorkExperience: 'Enhanced work experience',
      untitledResume: 'Untitled Resume',
      applied: 'Applied',
      saved: 'Saved'
    },
    ar: {
      profile: 'الملف الشخصي',
      proMember: 'بريميوم',
      basicMember: 'أساسي',
      freePlan: 'خطة مجانية',
      accountStats: 'إحصائيات الحساب',
      resumes: 'السير الذاتية',
      savedJobs: 'الوظائف المحفوظة',
      applications: 'الطلبات',
      recentActivity: 'النشاط الأخير',
      viewProfile: 'عرض الملف الشخصي',
      editProfile: 'تعديل الملف الشخصي',
      upgradeNow: 'ترقية الخطة',
      unlockFeatures: 'احصل على سير ذاتية غير محدودة وميزات الذكاء الاصطناعي والدعم ذو الأولوية',
      noActivity: 'لا يوجد نشاط حديث',
      memberSince: 'عضو منذ',
      emailStatus: 'حالة البريد',
      verified: 'موثق',
      notVerified: 'غير موثق',
      quickStats: 'إحصائيات سريعة',
      quickActions: 'إجراءات سريعة',
      settings: 'الإعدادات',
      resumeUpdated: 'تم تحديث السيرة الذاتية',
      jobSaved: 'تم حفظ الوظيفة',
      aiEnhancement: 'تحسين بالذكاء الاصطناعي',
      enhancedWorkExperience: 'تم تحسين الخبرة العملية',
      untitledResume: 'سيرة ذاتية بدون عنوان',
      applied: 'تم التقديم',
      saved: 'محفوظة'
    }
  };

  const tr = translations[isRTL ? 'ar' : 'en'];

  // Get current plan from subscription context
  const currentPlan = getCurrentPlan();
  const isActivePlan = hasActiveSubscription();

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [profileData, cvCountData, jobStatsData, jobsData] = await Promise.all([
        userProfileApi.getProfile().catch(() => null),
        userProfileApi.getCvCount().catch(() => null),
        userJobsApi.getStatistics().catch(() => null),
        userJobsApi.getLibraryWithDetails(null, null, 5, 0).catch(() => ({ jobs: [] }))
      ]);
      
      // Set user profile data
      if (profileData) {
        setUserProfile(profileData);
      }

      // Set stats from API
      setStats({
        totalResumes: cvCountData?.cv_count || 0,
        savedJobs: jobStatsData?.total_saved || 0,
        applications: jobStatsData?.total_applied || 0
      });

      // Build recent activities from API data
      const activities = [];
      
      // Add recent saved jobs
      const jobs = jobsData?.jobs || jobsData || [];
      if (Array.isArray(jobs) && jobs.length > 0) {
        jobs.slice(0, 3).forEach((job) => {
          activities.push({
            id: `job-${job.id}`,
            icon: Bookmark,
            title: tr.jobSaved,
            subtitle: `${job.title || job.job_title || 'Job'} at ${job.company || job.company_name || 'Company'}`,
            time: getRelativeTime(job.saved_at || job.created_at),
            color: 'bg-[#90E0EF]'
          });
        });
      }

      // Add AI enhancement activity placeholder
      if (stats.totalResumes > 0) {
        activities.push({
          id: 'ai-enhance',
          icon: Sparkles,
          title: tr.aiEnhancement,
          subtitle: tr.enhancedWorkExperience,
          time: '2d ago',
          color: 'bg-[#F9A826]'
        });
      }

      setRecentActivities(activities.slice(0, 5));
    } catch (error) {
      // Silently handle errors - sidebar data is not critical
      setStats({
        totalResumes: 0,
        savedJobs: 0,
        applications: 0
      });
      setRecentActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const getRelativeTime = (dateString) => {
    if (!dateString) return 'Recently';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return `${Math.floor(diffInSeconds / 604800)}w ago`;
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const statItems = [
    { icon: FileText, label: tr.resumes, value: stats.totalResumes, color: 'bg-[#E0F2FF]', iconColor: 'text-[#2F80ED]' },
    { icon: Bookmark, label: tr.savedJobs, value: stats.savedJobs, color: 'bg-[#FFD6A5]', iconColor: 'text-[#F9A826]' },
    { icon: CheckCircle, label: tr.applications, value: stats.applications, color: 'bg-[#B7E4C7]', iconColor: 'text-[#27AE60]' }
  ];

  return (
    <div 
      className="w-full flex flex-col bg-white"
    >
      {/* Header with Profile Photo and Close Button - More Compact */}
      <div className="relative px-4 pt-4 pb-3 border-b border-[#E5E7EB]">
        {/* Close Button - Absolute Position */}
        <motion.button
          whileHover={{ scale: 1.1, backgroundColor: '#F5F5F5' }}
          whileTap={{ scale: 0.9 }}
          onClick={onClose}
          className={`absolute top-3 ${isRTL ? 'left-3' : 'right-3'} p-1 rounded-lg transition-colors z-10`}
        >
          <X className="w-4 h-4 text-[#6B7280]" />
        </motion.button>

        {/* User Profile - Compact Horizontal Layout */}
        <div className={`flex items-center gap-3 pr-8 ${isRTL ? 'flex-row-reverse pl-8 pr-0' : ''}`}>
          <div className="relative flex-shrink-0">
            {/* Profile Picture - Smaller */}
            <div className="w-12 h-12 rounded-lg bg-[#2F80ED] flex items-center justify-center text-white text-base font-bold overflow-hidden shadow-sm">
              {currentUser?.photoURL ? (
                <img
                  src={currentUser.photoURL}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-6 h-6 text-white" />
              )}
            </div>
            {/* Logo Badge - Tiny */}
            <div className={`absolute -bottom-0.5 ${isRTL ? '-left-0.5' : '-right-0.5'} w-5 h-5 bg-white rounded-full border border-[#E5E7EB] p-0.5 flex items-center justify-center`}>
              <img 
                src="/cvlogs-logo.png" 
                alt="CVLogs" 
                className="w-full h-full object-contain"
              />
            </div>
          </div>
          
          <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : 'text-left'}`}>
            {/* User Full Name */}
            <h3 className="text-sm font-bold text-[#333333] truncate">
              {userProfile?.name || currentUser?.displayName || 'User'}
            </h3>
            
            {/* Email */}
            <p className="text-xs text-[#6B7280] truncate">
              {userProfile?.email || currentUser?.email}
            </p>

            {/* Plan Badge - Inline */}
            <div className="mt-1">
              {currentPlan === 'premium' ? (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gradient-to-r from-[#2F80ED] to-[#1E6FCC] text-white text-xs font-semibold rounded">
                  <Crown className="w-2.5 h-2.5" />
                  {tr.proMember}
                </span>
              ) : currentPlan === 'basic' ? (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gradient-to-r from-[#18212F] to-[#0F1419] text-white text-xs font-semibold rounded">
                  <Star className="w-2.5 h-2.5" />
                  {tr.basicMember}
                </span>
              ) : (
                <span className="inline-flex items-center px-1.5 py-0.5 bg-[#F5F5F5] text-[#6B7280] text-xs font-medium rounded">
                  {tr.freePlan}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats - Compact */}
      <div className="px-4 py-3">
        {loading ? (
          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-[#F5F5F5] rounded-lg animate-pulse"></div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            <motion.div
              whileHover={{ y: -2, scale: 1.02 }}
              className="text-center p-2 bg-[#F5F5F5] rounded-lg hover:bg-[#E0F2FF] hover:shadow-sm transition-all cursor-pointer"
              onClick={() => {
                navigate('/cv-builder');
                onClose();
              }}
            >
              <FileText className="w-4 h-4 text-[#2F80ED] mx-auto mb-1" />
              <p className="text-base font-bold text-[#333333]">{stats.totalResumes}</p>
              <p className="text-xs text-[#6B7280] truncate">{tr.resumes}</p>
            </motion.div>

            <motion.div
              whileHover={{ y: -2, scale: 1.02 }}
              className="text-center p-2 bg-[#F5F5F5] rounded-lg hover:bg-[#FFF7ED] hover:shadow-sm transition-all cursor-pointer"
              onClick={() => {
                navigate('/job-library');
                onClose();
              }}
            >
              <Bookmark className="w-4 h-4 text-[#F9A826] mx-auto mb-1" />
              <p className="text-base font-bold text-[#333333]">{stats.savedJobs}</p>
              <p className="text-xs text-[#6B7280] truncate">{tr.saved}</p>
            </motion.div>

            <motion.div
              whileHover={{ y: -2, scale: 1.02 }}
              className="text-center p-2 bg-[#F5F5F5] rounded-lg hover:bg-[#F0FDF4] hover:shadow-sm transition-all cursor-pointer"
              onClick={() => {
                navigate('/job-library');
                onClose();
              }}
            >
              <CheckCircle className="w-4 h-4 text-[#27AE60] mx-auto mb-1" />
              <p className="text-base font-bold text-[#333333]">{stats.applications}</p>
              <p className="text-xs text-[#6B7280] truncate">{tr.applied}</p>
            </motion.div>
          </div>
        )}
      </div>

      {/* Quick Actions - Compact */}
      <div className="px-4 py-3 border-t border-[#E5E7EB] space-y-1">
        <motion.button
          whileHover={{ x: isRTL ? -2 : 2, backgroundColor: '#F5F5F5' }}
          whileTap={{ scale: 0.98 }}
          onClick={() => {
            navigate('/settings');
            onClose();
          }}
          className="w-full flex items-center gap-2 px-2.5 py-2 text-left rounded-lg transition-colors"
        >
          <Settings className="w-4 h-4 text-[#6B7280]" />
          <span className="text-sm font-medium text-[#333333]">{tr.settings}</span>
        </motion.button>

        {currentPlan === 'free' && (
          <motion.button
            whileHover={{ x: isRTL ? -2 : 2, scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              navigate('/settings', { state: { tab: 'subscription' } });
              onClose();
            }}
            className="w-full flex items-center gap-2 px-2.5 py-2 text-left bg-gradient-to-r from-[#2F80ED] to-[#1E6FCC] hover:from-[#1E6FCC] hover:to-[#0F5FB8] rounded-lg transition-all shadow-sm"
          >
            <Zap className="w-4 h-4 text-white" />
            <span className="text-sm font-semibold text-white">{tr.upgradeNow}</span>
          </motion.button>
        )}
      </div>

      {/* Footer Info - Minimal */}
      <div className="px-4 py-2.5 border-t border-[#E5E7EB] bg-[#FAFAFA]">
        <div className="flex items-center justify-between text-xs text-[#6B7280]">
          <span className="font-medium">{tr.memberSince}</span>
          {userProfile?.created_at && (
            <span className="font-semibold text-[#333333]">
              {new Date(userProfile.created_at).toLocaleDateString('en-US', { 
                month: 'short', 
                year: 'numeric' 
              })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
